import React, { useEffect, useState, useCallback, useRef } from "react";
import api, { apiError, API, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { IdCard, Plus, Pencil, Trash2, Phone, Camera, MapPin, IndianRupee } from "lucide-react";

export default function OfficeStaff() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const token = localStorage.getItem("token");
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", designation: "", phone: "", office: "", bio: "", salary: "", commission: "", login_email: "", login_password: "", is_accountant: false });
  const [photo, setPhoto] = useState(null);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => setStaff((await api.get("/staff")).data), []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditId(null); setForm({ name: "", designation: "", phone: "", office: "", bio: "", salary: "", commission: "", login_email: "", login_password: "", is_accountant: false }); setPhoto(null); setOpen(true); };
  const openEdit = (s) => { setEditId(s.id); setForm({ name: s.name, designation: s.designation || "", phone: s.phone || "", office: s.office || "", bio: s.bio || "", salary: s.salary || "", commission: s.commission || "", login_email: s.login_email || "", login_password: "", is_accountant: !!s.is_accountant }); setPhoto(null); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("workspace_id", localStorage.getItem("workspace_id"));
      fd.append("name", form.name);
      fd.append("designation", form.designation);
      fd.append("phone", form.phone);
      fd.append("office", form.office);
      fd.append("bio", form.bio);
      fd.append("salary", form.salary || 0);
      fd.append("commission", form.commission || 0);
      fd.append("login_email", form.login_email);
      fd.append("is_accountant", form.is_accountant ? "true" : "false");
      if (form.login_password) fd.append("login_password", form.login_password);
      if (photo) fd.append("photo", photo);
      if (editId) await api.put(`/staff/${editId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      else await api.post("/staff", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(editId ? "Staff updated" : "Staff added");
      setOpen(false); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const pay = async (id, kind) => {
    try { const { data } = await api.post(`/staff/${id}/pay?kind=${kind}`); toast.success(`${kind === "salary" ? "Salary" : "Commission"} ₹${data.amount} paid — added to P&L`); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/staff/${id}`); setStaff((p) => p.filter((s) => s.id !== id)); toast.success("Staff removed"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader eyebrow="Workspace" title="Office Staff"
        subtitle="Meet Our Team — staff directory with photos & designations, shown on the website"
        action={isOwner && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditId(null); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-staff-btn" onClick={openNew} className="bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold gap-2"><Plus className="w-4 h-4" /> Add Staff</Button>
            </DialogTrigger>
            <DialogContent className="bg-[#05261D] border-gold/20">
              <DialogHeader>
                <DialogTitle className="font-serif text-gold flex items-center gap-2"><IdCard className="w-5 h-5" /> {editId ? "Edit Staff" : "Add Staff"}</DialogTitle>
                <DialogDescription>Add a team member with their role, branch and photo.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-xs text-muted-foreground">Full name</Label><Input data-testid="staff-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-card/60" /></div>
                <div><Label className="text-xs text-muted-foreground">Designation</Label><Input data-testid="staff-designation-input" value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} className="mt-1 bg-card/60" placeholder="e.g. Branch Manager, Visa Officer" /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-xs text-muted-foreground">Mobile</Label><Input data-testid="staff-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 bg-card/60" /></div>
                  <div><Label className="text-xs text-muted-foreground">Branch / Office</Label><Input data-testid="staff-office-input" value={form.office} onChange={(e) => setForm({ ...form, office: e.target.value })} className="mt-1 bg-card/60" placeholder="e.g. Kolkata" /></div>
                </div>
                <div><Label className="text-xs text-muted-foreground">Short bio</Label><Textarea data-testid="staff-bio-input" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="mt-1 bg-card/60 min-h-[60px]" /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-xs text-muted-foreground">Monthly salary (₹)</Label><Input data-testid="staff-salary-input" type="number" min="0" value={form.salary} onChange={(e) => setForm({ ...form, salary: e.target.value })} className="mt-1 bg-card/60" /></div>
                  <div><Label className="text-xs text-muted-foreground">Commission (₹)</Label><Input data-testid="staff-commission-input" type="number" min="0" value={form.commission} onChange={(e) => setForm({ ...form, commission: e.target.value })} className="mt-1 bg-card/60" /></div>
                </div>
                <label className="flex items-center gap-2 text-sm text-white"><input type="checkbox" data-testid="staff-accountant-checkbox" checked={form.is_accountant} onChange={(e) => setForm({ ...form, is_accountant: e.target.checked })} /> Accountant — can see the Accounts transaction list after staff login</label>
                <div className="border-t border-border/50 pt-3">
                  <Label className="text-xs text-gold">Login credentials (optional)</Label>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    <Input data-testid="staff-login-email" value={form.login_email} onChange={(e) => setForm({ ...form, login_email: e.target.value })} className="bg-card/60" placeholder="login email" />
                    <Input data-testid="staff-login-password" type="password" value={form.login_password} onChange={(e) => setForm({ ...form, login_password: e.target.value })} className="bg-card/60" placeholder={editId ? "new password" : "password"} />
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">Staff sign in at /staff-login with these.</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Photo</Label>
                  <Button type="button" variant="outline" data-testid="staff-photo-btn" onClick={() => fileRef.current?.click()} className="mt-1 w-full border-gold/40 text-gold gap-2"><Camera className="w-4 h-4" /> {photo ? photo.name : "Choose photo"}</Button>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => setPhoto(e.target.files?.[0] || null)} />
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="save-staff-btn" onClick={save} disabled={saving || !form.name} className="bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold">{saving ? "Saving…" : "Save Staff"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )} />

      <div className="p-4 sm:p-8">
        {staff.length === 0 && (
          <Card className="p-8 bg-card/40 border-border/60 text-center">
            <IdCard className="w-8 h-8 text-gold mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No staff added yet. {isOwner ? "Click “Add Staff” to build your Meet Our Team directory." : "Ask the admin to add staff."}</p>
          </Card>
        )}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {staff.map((s) => (
            <Card key={s.id} data-testid={`staff-card-${s.id}`} className="p-5 bg-card/40 border-border/60 card-hover flex flex-col items-center text-center">
              {s.photo_path ? (
                <img src={`${API}/staff/${s.id}/photo?auth=${token}`} alt={s.name} className="w-20 h-20 rounded-full object-cover ring-2 ring-gold/50" />
              ) : (
                <div className="w-20 h-20 rounded-full bg-primary/50 border border-gold/30 flex items-center justify-center text-2xl font-serif text-gold">{s.name[0]}</div>
              )}
              <p className="font-serif font-semibold text-white mt-3">{s.name}</p>
              {s.designation && <Badge variant="outline" className="border-gold/40 text-gold mt-1">{s.designation}</Badge>}
              {s.office && <p className="text-[11px] text-muted-foreground mt-1.5 flex items-center gap-1"><MapPin className="w-3 h-3 text-gold" /> {s.office}</p>}
              {s.phone && <p className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1"><Phone className="w-3 h-3 text-gold" /> {s.phone}</p>}
              {s.bio && <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{s.bio}</p>}
              {isOwner && (s.salary > 0 || s.commission > 0) && (
                <div className="flex gap-2 mt-3 w-full">
                  {s.salary > 0 && <div className="flex-1 bg-primary/40 rounded-lg p-2"><p className="text-[9px] text-muted-foreground">Salary</p><p className="text-xs font-mono text-gold">{inr(s.salary)}</p></div>}
                  {s.commission > 0 && <div className="flex-1 bg-primary/40 rounded-lg p-2"><p className="text-[9px] text-muted-foreground">Commission</p><p className="text-xs font-mono text-gold">{inr(s.commission)}</p></div>}
                </div>
              )}
              {isOwner && s.has_login && <Badge variant="outline" className="border-emerald-400/40 text-emerald-300 text-[9px] mt-2">has login</Badge>}
              {isOwner && (
                <div className="flex flex-wrap gap-1.5 mt-3 justify-center">
                  {s.salary > 0 && <Button size="sm" data-testid={`pay-salary-${s.id}`} onClick={() => pay(s.id, "salary")} className="bg-gold text-[#0a2e22] hover:bg-gold/90 text-[10px] h-7 gap-1"><IndianRupee className="w-3 h-3" /> Pay Salary</Button>}
                  {s.commission > 0 && <Button size="sm" data-testid={`pay-commission-${s.id}`} onClick={() => pay(s.id, "commission")} className="bg-gold/80 text-[#0a2e22] hover:bg-gold text-[10px] h-7 gap-1"><IndianRupee className="w-3 h-3" /> Pay Commission</Button>}
                  <Button size="icon" variant="ghost" data-testid={`edit-staff-${s.id}`} onClick={() => openEdit(s)} className="h-7 w-7 text-gold hover:bg-gold/15"><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button size="icon" variant="ghost" data-testid={`delete-staff-${s.id}`} onClick={() => remove(s.id)} className="h-7 w-7 text-destructive hover:bg-destructive/15"><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              )}
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
